import React from 'react';
import {
  Box, Paper, Typography, Chip, Divider, Stack, Grid, LinearProgress, Alert,
} from '@mui/material';
import {
  EmojiObjects as LightbulbIcon,
  TrendingUp as TrendIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { TrainingInsights, scoreColor, barColor } from './types';

interface Props {
  insights: TrainingInsights;
}

/** One dimension's score card. Shows the section name + overall score.
 *  A null/undefined score means N/A (the track had no items of this
 *  type, or the dimension didn't run) — rendered as a muted card. */
const DimensionCard: React.FC<{
  title: string;
  score: number | null | undefined;
  oneLineSummary?: string;
  disabled?: boolean;
}> = ({ title, score, oneLineSummary, disabled }) => {
  if (disabled || score == null) {
    return (
      <Paper variant="outlined" sx={{ p: 2, opacity: 0.5 }}>
        <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          N/A — not applicable for this session
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2">{title}</Typography>
        <Chip label={`${score}%`} size="small" color={scoreColor(score)} />
      </Box>
      <LinearProgress
        variant="determinate"
        value={score}
        color={barColor(score)}
        sx={{ height: 6, borderRadius: 1, mb: 1 }}
      />
      {oneLineSummary && (
        <Typography variant="caption" color="text.secondary">
          {oneLineSummary}
        </Typography>
      )}
    </Paper>
  );
};

const TrainingInsightsSummary: React.FC<Props> = ({ insights }) => {
  const oc = insights.overallCoaching;
  const playbookEnabled = insights.playbookCoverage?.wasUsed ?? false;

  return (
    <Box>
      {/* ── Headline behavioral score ───────────────────────────────── */}
      {insights.wasAbandoned && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Session Abandoned</strong> — the rep disconnected before
          completing the training. Items not attempted are listed in the
          Details tab without a score. The overall score reflects only the
          items the rep actually engaged with.
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="h6">Overall Behavioral Score</Typography>
              {insights.wasAbandoned && (
                <Chip label="Session Abandoned" color="warning" size="small" variant="outlined" />
              )}
            </Box>
            {oc?.managerSummary && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {oc.managerSummary}
              </Typography>
            )}
          </Box>
          {oc && (
            <Chip
              label={`${oc.behavioralScore}%`}
              color={scoreColor(oc.behavioralScore)}
              sx={{ fontSize: '1.25rem', height: 40, px: 1.5 }}
            />
          )}
        </Box>
      </Paper>

      {/* ── 5 summary bullets ──────────────────────────────────────── */}
      {(insights.trainingSummaryBullets ?? []).length > 0 && (
        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <LightbulbIcon color="info" fontSize="small" />
            <Typography variant="h6">Session Highlights</Typography>
          </Box>
          <Stack spacing={1.5}>
            {insights.trainingSummaryBullets!.map((b, idx) => (
              <Box key={idx} display="flex" gap={1}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 16 }}>
                  {idx + 1}.
                </Typography>
                <Typography variant="body2">{b}</Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* ── Dimension cards ─────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Dimension Scores
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DimensionCard
              title="Objection & FAQ"
              score={insights.objectionFaqHandling?.overallScore}
              oneLineSummary={insights.objectionFaqHandling?.overallSummary}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DimensionCard
              title="Landmine"
              score={insights.landmineHandling?.overallScore}
              oneLineSummary={insights.landmineHandling?.overallSummary}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DimensionCard
              title="Competitor"
              score={insights.competitorHandling?.overallScore}
              oneLineSummary={insights.competitorHandling?.overallSummary}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DimensionCard
              title="Playbook Coverage"
              score={insights.playbookCoverage?.overallScore}
              oneLineSummary={insights.playbookCoverage?.overallSummary}
              disabled={!playbookEnabled}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DimensionCard
              title="KB Progression"
              score={
                insights.salesIntelligence?.kbItemProgression?.length
                  ? Math.round(
                      insights.salesIntelligence.kbItemProgression.reduce(
                        (acc, i) => acc + (i.handlingScore || 0), 0
                      ) / insights.salesIntelligence.kbItemProgression.length
                    )
                  : undefined
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DimensionCard
              title="Tone"
              score={(() => {
                const t = insights.toneAnalysis;
                if (!t) return undefined;
                const pos = (t.positive.confidence.score + t.positive.optimism.score) / 2;
                const neg = (t.negative.defensiveness.score + t.negative.confrontation.score) / 2;
                return Math.round(Math.max(0, Math.min(100, pos - neg / 2 + 25)));
              })()}
              oneLineSummary={insights.toneAnalysis?.overallCoaching}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Strengths + top action items ─────────────────────────── */}
      {oc && (oc.strengths?.length > 0 || oc.improvements?.length > 0) && (
        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
          <Grid container spacing={3}>
            {oc.strengths?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  Strengths
                </Typography>
                {oc.strengths.map((s, idx) => (
                  <Box key={idx} display="flex" gap={1} mb={0.5} alignItems="flex-start">
                    <CheckIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
                    <Typography variant="body2">{s}</Typography>
                  </Box>
                ))}
              </Grid>
            )}
            {oc.improvements?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                  Top action items
                </Typography>
                {oc.improvements.slice(0, 3).map((imp, idx) => (
                  <Box key={idx} display="flex" gap={1} mb={0.5} alignItems="flex-start">
                    <TrendIcon color="warning" fontSize="small" sx={{ mt: 0.25 }} />
                    <Typography variant="body2">{imp}</Typography>
                  </Box>
                ))}
              </Grid>
            )}
          </Grid>
          {oc.recommendedNextTrack && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Recommended next track:
              </Typography>
              <Typography variant="body2">{oc.recommendedNextTrack}</Typography>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default TrainingInsightsSummary;
